import { Anchor, type AnchorProps, Button, type ButtonProps, NavLink, type NavLinkProps } from "@mantine/core";
import { createLink, useRouter } from "@tanstack/react-router";
import { type ComponentPropsWithoutRef, type CSSProperties, forwardRef, type ReactNode } from "react";

// Mantine components as router links that keep TanStack Router's typed `to` / `search`
const MantineAnchor = forwardRef<HTMLAnchorElement, AnchorProps & ComponentPropsWithoutRef<"a">>((props, ref) => (
  <Anchor component="a" ref={ref} {...props} />
));
const MantineNavLink = forwardRef<HTMLAnchorElement, NavLinkProps & ComponentPropsWithoutRef<"a">>((props, ref) => (
  <NavLink component="a" ref={ref} {...props} />
));

const MantineButton = forwardRef<HTMLAnchorElement, ButtonProps & ComponentPropsWithoutRef<"a">>((props, ref) => (
  <Button component="a" ref={ref} {...props} />
));

export const AnchorLink = createLink(MantineAnchor);
/** A Mantine button that is a router link (typed ``to`` / ``search``; middle-click opens a tab). */
export const ButtonLink = createLink(MantineButton);
export const NavLinkLink = createLink(MantineNavLink);

/** A link to a stored in-app URL (pins, recent items, search hits): client-side navigation, real href for new tabs. */
export function HrefLink({
  href,
  className,
  children,
  style,
}: {
  href: string;
  className?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const router = useRouter();
  return (
    <a
      href={href}
      className={className}
      style={style}
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        void router.navigate({ href });
      }}
    >
      {children}
    </a>
  );
}
